import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import { selectDarkMode } from '../../store/selector/theme.selector';
import { setDarkMode } from '../../store/action/theme.action';
import { ThemeState } from '../../store/model/theme.model';
import { ThemeInitializerService } from '../../utils/theme-initializer.service';
import { SidebarComponent } from "../sidebar/sidebar.component";
import { AuthService } from '../../auth/auth.service';
import { selectUser } from '../../auth/store/auth.selector';
import { AuthState } from '../../auth/store/auth.model';
import { Roles } from '../../providers/static-data';
import { ProfileApiService } from '../../pages/profile/profile-api.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterModule, CommonModule, SidebarComponent],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss'
})
export class NavbarComponent {

  private readonly defaultProfileUrl = 'https://media.istockphoto.com/id/2151669184/vector/vector-flat-illustration-in-grayscale-avatar-user-profile-person-icon-gender-neutral.jpg?s=612x612&w=0&k=20&c=UEa7oHoOL30ynvmJzSCIPrwwopJdfqzBs0q69ezQoM8=';
  profileUrl = this.defaultProfileUrl;
  navbarName = 'User';
  navbarEmail = '';
  navbarRoleId: number | string | null = null;
  isDarkMode$: Observable<ThemeState['darkMode']>;
 user$: Observable<AuthState['user']>;
  private readonly profileNameUpdatedHandler = (event: Event) => {
    const customEvent = event as CustomEvent<{ name?: string }>;
    this.navbarName = customEvent.detail?.name || this.getStoredUserName() || this.navbarName;
  };

  constructor(
    private store: Store,
    private themeService: ThemeInitializerService,
    private authService:AuthService,
    private profileApi: ProfileApiService
  ) {
    this.isDarkMode$ = store.select(selectDarkMode);
    this.user$ = this.store.select(selectUser);

  }

  ngOnInit() {
    window.addEventListener('profileNameUpdated', this.profileNameUpdatedHandler);
    this.store.select(selectUser).pipe(take(1)).subscribe({
      next: (user) => {
        this.navbarName = user?.name || this.getStoredUserName() || 'User';
        this.navbarEmail = user?.email || this.getStoredUserEmail() || '';
        this.navbarRoleId = user?.role_id ?? this.getStoredUserRoleId();

        const userId = localStorage.getItem('master_user_id') ?? user?.id ?? this.getStoredUserId();
        if (userId) {
          this.loadNavbarProfile(userId);
        }
      }
    });
  }

  ngOnDestroy() {
    window.removeEventListener('profileNameUpdated', this.profileNameUpdatedHandler);
  }

  toggleDarkMode() {
    this.isDarkMode$.pipe(take(1)).subscribe((darkMode) => {
      this.themeService.updateTheme({ darkMode: !darkMode });
      if (darkMode) {
        document.documentElement.dataset['theme'] = 'dark';
      } else {
        document.documentElement.dataset['theme'] = 'light';
      }


    });
  }

  getRoleLabel(roleId: number | string | undefined | null): string {
    if (roleId === undefined || roleId === null) {
      return 'Unknown';
    }
    const normalizedId = roleId.toString();
    return Roles.find(role => role.value.toString() === normalizedId)?.label || `Role ${normalizedId}`;
  }

  logout() {
    this.authService.logout();
  }

  private loadNavbarProfile(userId: number | string) {
    this.profileApi.getUserById(userId).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? {};
        const name = data?.name || this.buildFullName(data);
        const image = data?.profile_photo || data?.image || data?.profile_image || '';

        this.navbarName = name || this.navbarName;
        this.navbarEmail = data?.email || data?.email_id || this.navbarEmail;
        this.navbarRoleId = data?.role_id ?? data?.role?.id ?? this.navbarRoleId;
        this.profileUrl = this.resolveProfileImageUrl(image) || this.profileUrl;
      }
    });
  }

  private buildFullName(data: any): string {
    return [data?.first_name, data?.middle_name, data?.last_name]
      .map(part => (part ?? '').toString().trim())
      .filter(Boolean)
      .join(' ');
  }

  private resolveProfileImageUrl(path: string): string {
    if (!path) {
      return '';
    }
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
      return path;
    }
    return path.replace(/^\//, '');
  }

  private getStoredAuthUser(): any {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return null;
    }

    try {
      const parsed = JSON.parse(authData);
      return parsed?.user ?? parsed;
    } catch {
      return null;
    }
  }

  private getStoredUserId(): number | string | null {
    return this.getStoredAuthUser()?.id ?? null;
  }

  private getStoredUserName(): string {
    const user = this.getStoredAuthUser();
    const fullName = this.buildFullName(user);
    return user?.name || fullName || '';
  }

  private getStoredUserEmail(): string {
    const user = this.getStoredAuthUser();
    return user?.email || user?.email_id || '';
  }

  private getStoredUserRoleId(): number | string | null {
    return this.getStoredAuthUser()?.role_id ?? null;
  }

}
